import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Card } from './ui/card';
import { Button } from './ui/button';
import { CheckCircle, Loader2, XCircle, PartyPopper } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const SubscriptionSuccess = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { refreshUser } = useAuth();
  const [status, setStatus] = useState('checking'); // checking, success, error
  const [attempts, setAttempts] = useState(0);
  const sessionId = searchParams.get('session_id');

  useEffect(() => {
    if (sessionId) {
      checkPaymentStatus();
    } else {
      setStatus('error');
    }
  }, [sessionId]);

  const checkPaymentStatus = async () => {
    const maxAttempts = 5;
    const pollInterval = 2000;

    if (attempts >= maxAttempts) {
      setStatus('error');
      return;
    }

    try {
      const response = await axios.get(
        `${API}/subscriptions/checkout-status/${sessionId}`,
        { withCredentials: true }
      );

      if (response.data.payment_status === 'paid') {
        setStatus('success');
        // Refresh user data to get updated subscription
        if (refreshUser) {
          await refreshUser();
        }
        return;
      } else if (response.data.status === 'expired') {
        setStatus('error');
        return;
      }

      // Continue polling
      setAttempts(prev => prev + 1);
      setTimeout(checkPaymentStatus, pollInterval);
    } catch (error) {
      console.error('Error checking payment status:', error);
      setAttempts(prev => prev + 1);
      setTimeout(checkPaymentStatus, pollInterval);
    }
  };

  if (status === 'checking') {
    return (
      <div className="min-h-screen bg-gradient-to-b from-purple-50 to-white flex items-center justify-center p-4">
        <Card className="p-8 text-center max-w-md w-full">
          <Loader2 className="w-16 h-16 text-purple-600 animate-spin mx-auto mb-4" />
          <h2 className="text-2xl font-bold text-gray-900 mb-2">Verificando pagamento...</h2>
          <p className="text-gray-600">Aguarde enquanto confirmamos seu pagamento.</p>
        </Card>
      </div>
    );
  }

  if (status === 'error') {
    return (
      <div className="min-h-screen bg-gradient-to-b from-purple-50 to-white flex items-center justify-center p-4">
        <Card className="p-8 text-center max-w-md w-full">
          <XCircle className="w-16 h-16 text-red-500 mx-auto mb-4" />
          <h2 className="text-2xl font-bold text-gray-900 mb-2">Algo deu errado</h2>
          <p className="text-gray-600 mb-6">
            Não foi possível confirmar seu pagamento. Se o valor foi debitado, entre em contato com nosso suporte.
          </p>
          <div className="space-y-3">
            <Button onClick={() => navigate('/subscription/plans')} className="w-full">
              Tentar novamente
            </Button>
            <Button onClick={() => navigate('/dashboard')} variant="outline" className="w-full">
              Ir para o Dashboard
            </Button>
          </div>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-b from-purple-50 to-white flex items-center justify-center p-4">
      <Card className="p-8 text-center max-w-md w-full">
        <div className="relative inline-block mb-4">
          <CheckCircle className="w-20 h-20 text-green-500 mx-auto" />
          <PartyPopper className="w-8 h-8 text-yellow-500 absolute -top-2 -right-2 animate-bounce" />
        </div>
        
        <h2 className="text-3xl font-bold text-gray-900 mb-2">Parabéns! 🎉</h2>
        <p className="text-xl text-gray-700 mb-2">Sua assinatura foi ativada!</p>
        <p className="text-gray-600 mb-8">
          Agora você tem acesso completo à plataforma Simby. Aproveite sua jornada de desenvolvimento da inteligência relacional!
        </p>
        
        <Button 
          onClick={() => navigate('/dashboard')}
          size="lg"
          className="w-full bg-purple-600 hover:bg-purple-700"
        >
          Começar agora
        </Button>
        
        <p className="text-sm text-gray-500 mt-4">
          Um e-mail de confirmação foi enviado para você.
        </p>
      </Card>
    </div>
  );
};

export default SubscriptionSuccess;

